import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {PsFile} from '../../user/components/public-services/ps-file';
import {ContactModal} from '../../user/components/public-services/contact-modal/contact-modal';

export enum PsServiceTypeEnum {
  DiplomaDuplicate,
  TransferApplication,
  Hostel
}

@Injectable()
export class PublicServicesService {

  constructor(private http: HttpClient) {
  }

  getDiplomaDuplicateApplications(params) {
    return this.http.post<any>('api/diploma-duplicate/applications', params);
  }

  getProcessingTimeStatus() {
    return this.http.get<any>('api/diploma-duplicate/dic/processing-time-status');
  }

  getApplicationStatus() {
    return this.http.get<any>('api/diploma-duplicate/dic/application-status');
  }

  saveContactDiplomaDuplicate(contact, appId: number) {
    return this.http.post<any>('api/diploma-duplicate/saveContact/' + appId, contact);
  }

  getDiplomaDuplicateApplication(appId: number) {
    return this.http.get<any>('api/diploma-duplicate/application/' + appId);
  }

  getFileInfo(fileId) {
    return this.http.get<PsFile>('api/diploma-duplicate/getFileInfo/' + fileId);
  }

  getFile(fileInfo: PsFile) {
    return this.http.post('api/diploma-duplicate/getFile/', fileInfo, {responseType: 'blob'});
  }

  getContactByAppId(appId: number, psType: PsServiceTypeEnum) {
    if (psType === PsServiceTypeEnum.DiplomaDuplicate) {
      return this.http.get<any>('api/diploma-duplicate/application/contact/' + appId);
    } else if (psType === PsServiceTypeEnum.Hostel) {
      return this.http.get<any>('api/hostel/application/contact/' + appId);
    }
  }

  prepareXmlToSign(xmlDto, serviceType: PsServiceTypeEnum) {
    if (serviceType === PsServiceTypeEnum.DiplomaDuplicate) {
      return this.http.post<any>('api/diploma-duplicate/prepareXmlToSign', xmlDto);
    } else if (serviceType === PsServiceTypeEnum.TransferApplication) {
      return this.http.post<any>('api/transfer-application/prepareXmlToSign', xmlDto);
    } else if (serviceType === PsServiceTypeEnum.Hostel) {
      return this.http.post<any>('api/hostel/prepareXmlToSign', xmlDto);
    }
  }

  sendSignedXML(xml, serviceType: PsServiceTypeEnum, fd) {
    if (serviceType === PsServiceTypeEnum.DiplomaDuplicate) {
      return this.http.post<any>('api/diploma-duplicate/signedXML', fd, {headers: {'noContentType': 'true'}});
    } else if (serviceType === PsServiceTypeEnum.TransferApplication) {
      return this.http.post<any>('api/transfer-application/signedXML', xml);
    } else if (serviceType === PsServiceTypeEnum.Hostel) {
      return this.http.post<any>('api/hostel/signedXML', xml);
    }
  }

  processApplications(xml, serviceType: PsServiceTypeEnum) {
    if (serviceType === PsServiceTypeEnum.DiplomaDuplicate) {
      return this.http.post<any>('api/diploma-duplicate/processApplications/', xml);
    } else if (serviceType === PsServiceTypeEnum.TransferApplication) {
      return this.http.post<any>('api/transfer-application/processApplications/', xml);
    } else if (serviceType === PsServiceTypeEnum.Hostel) {
      return this.http.post<any>('api/hostel/processApplications/', xml);
    }
  }

  getProcessApplicationsCount() {
    return this.http.get<any>('api/diploma-duplicate/getProcessApplicationsCount');
  }

  getSeparatedUniversities(diplomaId: number) {
    return this.http.get<any>('api/diploma-duplicate/getSeparatedUniversities/' + diplomaId);
  }

  reSendToUniversity(diplomaId: number, univerId: number) {
    return this.http.get<any>('api/diploma-duplicate/reSendToUniversity/' + diplomaId + '/' + +univerId);
  }

  getProcessingTimeSettings() {
    return this.http.get<any>('api/processing-time/getProcessingTimeSettings');
  }

  saveProcessingTimeSetting(psTimeSettings) {
    return this.http.post<any>('api/processing-time/saveProcessingTimeSetting', psTimeSettings);
  }

  getTransferApplications(params) {
    return this.http.post<any>('api/transfer-application/applications', params);
  }

  getTransferApplication(appId: number) {
    return this.http.get<any>('api/transfer-application/application/' + appId);
  }

  getProcessTransferApplicationsCount() {
    return this.http.get<any>('api/transfer-application/getProcessApplicationsCount');
  }

  generateReport(params, serviceType: PsServiceTypeEnum) {
    if (serviceType === PsServiceTypeEnum.DiplomaDuplicate) {
      return this.http.post('api/diploma-duplicate/generateReport', params, {responseType: 'blob'});
    } else if (serviceType === PsServiceTypeEnum.TransferApplication) {
      return this.http.post('api/transfer-application/generateReport', params, {responseType: 'blob'});
    } else if (serviceType === PsServiceTypeEnum.Hostel) {
      return this.http.post('api/hostel/generateReport', params, {responseType: 'blob'});
    }
  }

  getDiplomaOtherApplications(params) {
    return this.http.post<any>('api/diploma-duplicate/other/applications', params);
  }

  saveDiplomaOtherApplication(app) {
    return this.http.post<any>(`api/diploma-duplicate/other/saveApplication`, app, {headers: {'noContentType': 'true'}});
  }

  deleteOtherApplication(id: number) {
    return this.http.delete<any>('api/diploma-duplicate/other/delete/' + id);
  }

  getEducationNotFinishedApplications(params) {
    return this.http.post<any>('api/ps-education-not-finished/applications', params);
  }

  saveEducationNotFinishedApplication(app) {
    return this.http.post<any>(`api/ps-education-not-finished/saveApplication`, app, {headers: {'noContentType': 'true'}});
  }

  deleteEducationNotFinishedApplication(id: number) {
    return this.http.delete<any>('api/ps-education-not-finished/delete/' + id);
  }

  downloadEducationNotFinishedFiles(params) {
    return this.http.post(`api/ps-education-not-finished/getFiles/`, params, {responseType: 'blob', observe: 'response'});
  }

  downloadDuplicateOtherFiles(params) {
    return this.http.post(`api/diploma-duplicate/other/getFiles/`, params, {responseType: 'blob', observe: 'response'});
  }

  downloadDiplomaFiles(params) {
    return this.http.post(`api/diploma-duplicate/getFiles/`, params, {responseType: 'blob', observe: 'response'});
  }

  getHostelApplications(params) {
    return this.http.post<any>('api/hostel/applications', params);
  }

  saveSettlingInfo(settingInfo, appId: number) {
    return this.http.post<any>('api/hostel/saveSettlingInfo/' + appId, settingInfo);
  }

  getHostelApplication(appId: number) {
    return this.http.get<any>('api/hostel/application/' + appId);
  }

  getSettlingInfoByAppId(appId: number) {
    return this.http.get<any>('api/hostel/application/settling/' + appId);
  }

  saveContact(contact: ContactModal, appId: number) {
    if (contact.psType === PsServiceTypeEnum.DiplomaDuplicate) {
      return this.http.post<any>('api/diploma-duplicate/saveContact/' + appId, contact);
    } else if (contact.psType === PsServiceTypeEnum.Hostel) {
      return this.http.post<any>('api/hostel/saveContact/' + appId, contact);
    }
  }

  checkContactsCount(appIds: number[]) {
    return this.http.post<any>('api/hostel/checkContactsCount', appIds);
  }

  approveCheckFields(appIds: number[]) {
    return this.http.post<any>('api/hostel/approveCheckFields', appIds);
  }

  getSettingsItemsAdmission(params) {
    return this.http.post<any>('api/items-admission/getItemsAdmission', params);
  }

  getSettingsItemsAdmissionById(id: number) {
    return this.http.get<any>('api/items-admission/getItemsAdmission/' + id);
  }

  getSettingsItemsAdmissionYearsDistinct() {
    return this.http.get<any>('api/items-admission/getYearsDistinct');
  }

  getSettingsItemsAdmissionSubjectTypes() {
    return this.http.get<any>('api/items-admission/getSubjectTypes');
  }

  saveSettingsItemsAdmission(item) {
    return this.http.post<any>('api/items-admission/save', item);
  }

  copyItemsAdmission(yearFrom: number, yearTo: number) {
    return this.http.get<any>('api/items-admission/copy/' + yearFrom + '/' + yearTo);
  }

  deleteSettingsItemsAdmission(id: number) {
    return this.http.delete<any>('api/items-admission/delete/' + id);
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/admin/services/public-services.service.ts